import React, { useState, useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Popover,
  Tabs,
  Tab,
  useTheme,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import aiavatar from "../assets/images/aiavatar.png";
import useravatar from "../assets/images/useravatar.png";
import closeicon from "../assets/images/closeicon.png";
import loadinggif from "../assets/images/loadinggif.gif";
import DataTable from "./DataTable";
import report_nexus from "../assets/images/report_nexus.svg";
import ReportDialog from "./ReportDialog";
import TextSelectionTooltip from "./TextSelectionTooltip";
import VisualizationChart from "./VisualizationChart";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SpeechManager from "../utils/speechManager";
import ImagePromptGenerator from "./ImagePromptGenerator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoIcon from "@mui/icons-material/Info";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

const NEGATIVE_FEEDBACK_OPTIONS = [
  "Incorrect answer",
  "Incomplete answer",
  "Not relevant",
  // "Wrong datasource used",
  "Poor explanation",
  "Formatting issue",
  "Other",
];

// Extracted MessageContent component to fix SonarQube issue
const MessageContent = ({
  isUser,
  isLoading,
  index,
  getMessageStyle,
  message,
  tooltipOpen,
  display_image_prompt,
  tooltipPosition,
  selectedText,
  closeTooltip,
  handleGenerateImagePrompt,
  imagePromptOpen,
  handleCloseImagePrompt,
  selectedPromptText,
  FileDatasourceSection,
  AdditionalResponsesPopover,
  ResponseTimeDisplay,
  onMessageLinkClick,
  visualization_payload,
}) => {

  // console.log("User:", isUser, "Rendering MessageContent for index:", index, "isLoading:", isLoading, "message:", message);
  if (isLoading) {
    return <img src={loadinggif} style={{ width: "50px" }} alt="loading" />;
  }

  return (
    <Box key={index}>
      <Box
        component="div"
        style={getMessageStyle()}
        onClick={onMessageLinkClick}
        sx={{
          overflowX: "auto",
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "10px",
            marginBottom: "10px",
          },
          "& th, & td": {
            border: "1px solid #e2e8f0",
            padding: "8px 10px",
            textAlign: "left",
            verticalAlign: "top",
            fontSize: "0.85rem",
          },
          "& th": {
            backgroundColor: "#f8fafc",
            fontWeight: 600,
          },
        }}
        dangerouslySetInnerHTML={{ __html: message }}
      />
      <TextSelectionTooltip
        open={tooltipOpen && display_image_prompt === "TRUE"}
        position={tooltipPosition}
        selectedText={selectedText}
        onClose={closeTooltip}
        onGenerateImagePrompt={handleGenerateImagePrompt}
      />
      <ImagePromptGenerator
        open={imagePromptOpen}
        onClose={handleCloseImagePrompt}
        initialText={selectedPromptText}
      />
      <FileDatasourceSection />
      <AdditionalResponsesPopover />
      <ResponseTimeDisplay isUser={isUser} />
      {visualization_payload && visualization_payload.visualization && (
        <VisualizationChart visualizationPayload={visualization_payload} />
      )}
    </Box>
  );
};

const ChatBubble = ({
  message,
  additionalAgentResponses, // Add prop for additional agent responses
  responseTime,
  citmessage,
  isUser,
  isSystemNotification,
  isLoading,
  handleCitationClickOpen,
  citationopen,
  handleCitationClose,
  citationclickvisible,
  tables = {},
  handleDownloadAll,
  isFirstMessage = false,
  toolResult,
  selectDatasourse,
  additional_agent_responses,
  reformatted_question,
  index,
  onRegenerateClick, // Prop for regenerate function
  isLastBotMessage, // Prop to determine if this is the last bot message
  onAbbreviationCandidateClick,
  questionId,
  threadId,
  feedbackType,
  feedbackReason,
  feedbackComment,
  onSubmitFeedback,
  visualization_payload,
  // additionalAgentResponses, // Add prop for additional agent responses
}) => {
  const bubbleId = useId(); // Generate unique ID for this bubble
  const messageRef = useRef(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [otherBubbleReading, setOtherBubbleReading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState("");
  const [imagePromptOpen, setImagePromptOpen] = useState(false);
  const [selectedPromptText, setSelectedPromptText] = useState("");
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedFeedbackReason, setSelectedFeedbackReason] = useState(feedbackReason || "");
  const [feedbackText, setFeedbackText] = useState(feedbackComment || "");
  const [feedbackError, setFeedbackError] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const toolResults = toolResult || [];
  const theme = useTheme();

  useEffect(() => {
    setSelectedFeedbackReason(feedbackReason || "");
    setFeedbackText(feedbackComment || "");
  }, [feedbackReason, feedbackComment]);

  // const display_image_prompt = "FALSE";
  const display_image_prompt = process.env.REACT_APP_ENABLE_IMAGE_PROMPT_GENERATION === "true" ? "TRUE" : "FALSE";
  // console.log("display_image_prompt:", display_image_prompt,typeof(display_image_prompt));
  // Handle info icon click
  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  // Handle info popover close
  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Check if additional data exists
  const hasAdditionalData =
    additionalAgentResponses &&
    (additionalAgentResponses.documents || additionalAgentResponses.tastewise);

  // Determine if the info popover is open
  const infoOpen = Boolean(infoAnchorEl);
  const infoId = infoOpen ? "additional-info-popover" : undefined;

  // Handle text selection
  const handleTextSelection = (event) => {
    if (isUser || isSystemNotification || isLoading) return;

    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    // Check if selection is within this ChatBubble
    if (!messageRef.current?.contains(selection.anchorNode)) return;

    if (selectedText && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setSelectedText(selectedText);
      setTooltipPosition({
        top: rect.top - messageRef.current.getBoundingClientRect().top,
        left:
          rect.left -
          messageRef.current.getBoundingClientRect().left + // Center the tooltip
          rect.width / 2,
      });
      setTooltipOpen(true);
    } else {
      closeTooltip();
    }
  };

  // Close tooltip
  const closeTooltip = () => {
    setTooltipOpen(false);
    setSelectedText("");
  };

  // Listen for clicks outside to close the tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        closeTooltip();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleReportDialogOpen = () => {
    setReportDialogOpen(true);
  };

  const handleReportDialogClose = () => {
    setReportDialogOpen(false);
  };

  const closeFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setFeedbackError("");
  };

  const handleFeedbackDialogClose = (event, reason) => {
    // Prevent accidental backdrop-triggered close while interacting with options.
    if (reason === "backdropClick") {
      return;
    }
    closeFeedbackDialog();
  };

  const handleThumbsUpClick = async () => {
    if (!questionId || !threadId || typeof onSubmitFeedback !== "function" || feedbackType === "up") {
      return;
    }

    setIsSubmittingFeedback(true);
    setFeedbackError("");
    const result = await onSubmitFeedback({
      questionId,
      threadId,
      feedbackType: "up",
      feedbackReason: null,
      feedbackComment: null,
    });
    setIsSubmittingFeedback(false);

    if (!result?.success) {
      setFeedbackError(result?.message || "Unable to save feedback.");
    }
  };

  const handleThumbsDownOpen = () => {
    if (!questionId || !threadId || typeof onSubmitFeedback !== "function") {
      return;
    }

    setSelectedFeedbackReason(feedbackReason || "");
    setFeedbackText(feedbackComment || "");
    setFeedbackError("");
    setFeedbackDialogOpen(true);
  };

  const handleThumbsDownSubmit = async () => {
    if (!selectedFeedbackReason) {
      setFeedbackError("Please select a feedback reason.");
      return;
    }

    if (selectedFeedbackReason === "Other" && !feedbackText.trim()) {
      setFeedbackError("Please enter feedback details for Other.");
      return;
    }

    setIsSubmittingFeedback(true);
    setFeedbackError("");
    const result = await onSubmitFeedback({
      questionId,
      threadId,
      feedbackType: "down",
      feedbackReason: selectedFeedbackReason,
      feedbackComment: feedbackText,
    });
    setIsSubmittingFeedback(false);

    if (!result?.success) {
      setFeedbackError(result?.message || "Unable to save feedback.");
      return;
    }

    closeFeedbackDialog();
  };

  const handleMessageLinkClick = (event) => {
    const linkElement = event.target.closest("a[data-abbreviation-candidate]");
    if (!linkElement) return;

    event.preventDefault();
    const selectedCandidate = linkElement.getAttribute("data-abbreviation-candidate");
    if (selectedCandidate && typeof onAbbreviationCandidateClick === "function") {
      onAbbreviationCandidateClick(selectedCandidate);
    }
  };

  //handle bot reply copy to clipboard with preserved formatting
  const handleCopy = () => {
    // Create a temporary element to hold the HTML content
    const tempElement = document.createElement("div");
    tempElement.innerHTML = message;

    // Try to copy as rich text if browser supports it
    if (navigator.clipboard && navigator.clipboard.write) {
      // Create a Blob containing rich text content
      const htmlBlob = new Blob([tempElement.innerHTML], { type: "text/html" });
      const plainTextBlob = new Blob([tempElement.innerText], {
        type: "text/plain",
      });

      try {
        navigator.clipboard
          .write([
            new ClipboardItem({
              "text/html": htmlBlob,
              "text/plain": plainTextBlob,
            }),
          ])
          .then(() => {
            // console.log("Formatted text copied to clipboard successfully!");
            setCopySuccess(true);
          })
          .catch((err) => {
            fallbackCopy(tempElement.innerText);
          });
      } catch (err) {
        fallbackCopy(tempElement.innerText);
      }
    } else {
      fallbackCopy(tempElement.innerText);
    }
  };

  // Fallback copy method using just plain text
  const fallbackCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Text copied to clipboard successfully!");
        setCopySuccess(true);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Handle closing the copy success notification
  const handleCloseCopyNotification = () => {
    setCopySuccess(false);
  };

  // Subscribe to speech manager to know when other bubbles are reading
  useEffect(() => {
    const unsubscribe = SpeechManager.subscribe((activeBubbleId) => {
      const isThisBubbleReading = activeBubbleId === bubbleId;
      setIsReading(isThisBubbleReading);
      setOtherBubbleReading(activeBubbleId !== null && !isThisBubbleReading);
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
      // If this bubble is reading when unmounting, stop it
      if (SpeechManager.isActive(bubbleId) && window.speechSynthesis) {
        SpeechManager.stopReading();
      }
    };
  }, [bubbleId]);

  // Effect to stop speech synthesis when messages change (like during chat reset)
  useEffect(() => {
    // If speech synthesis is active and index is 0 (first message), stop reading
    if (isReading && index === 0 && window.speechSynthesis) {
      SpeechManager.stopReading();
    }
  }, [index, isReading]);

  // Handle text-to-speech reading
  const handleReadAloud = () => {
    if ("speechSynthesis" in window) {
      if (isReading) {
        // Stop reading if already in progress
        SpeechManager.stopReading();
        console.log("Stopped reading bubble:", bubbleId);
      } else {
        // Force stop any ongoing speech and clear state
        SpeechManager.forceStopSpeech();

        // Create a new temporary element to strip HTML tags
        const tempElement = document.createElement("div");
        tempElement.innerHTML = message;
        const textToRead = tempElement.textContent || tempElement.innerText;

        console.log("Starting to read bubble:", bubbleId);

        // Register this bubble as the active one
        SpeechManager.startReading(bubbleId);

        const utterance = new SpeechSynthesisUtterance(textToRead);

        // Store the utterance in SpeechManager
        SpeechManager.setCurrentUtterance(utterance);

        // Ensure voices are loaded
        let voices = window.speechSynthesis.getVoices();
        if (voices.length === 0) {
          // If voices aren't loaded yet, wait for them
          window.speechSynthesis.onvoiceschanged = () => {
            voices = window.speechSynthesis.getVoices();
            setVoiceAndSpeak(utterance, voices);
          };
        } else {
          setVoiceAndSpeak(utterance, voices);
        }
      }
    } else {
      // Browser doesn't support speech synthesis
      console.log("Text-to-speech is not supported in your browser.");
    }
  };

  // Helper function to set voice and start speaking
  const setVoiceAndSpeak = (utterance, voices) => {
    // Try to find an English voice
    if (voices.length > 0) {
      const englishVoice = voices.find((voice) => voice.lang.startsWith("en-"));
      if (englishVoice) {
        utterance.voice = englishVoice;
      }
    }

    // Set speaking rate, pitch, etc.
    utterance.rate = 1.0; // Normal speed
    utterance.pitch = 1.0; // Normal pitch

    // Add event handlers
    utterance.onend = () => {
      console.log("Speech ended for bubble:", bubbleId);
      // Only stop if this is still the active bubble
      if (SpeechManager.isActive(bubbleId)) {
        SpeechManager.stopReading();
      }
    };

    utterance.onerror = (event) => {
      // Don't log or treat "interrupted" as an error since it's expected
      // when switching between bubbles
      if (event.error !== "interrupted") {
        console.error("Speech error:", event.error, "for bubble:", bubbleId);
      }

      // Only update UI if this is still the active bubble
      if (SpeechManager.isActive(bubbleId)) {
        SpeechManager.stopReading();
      }
    };

    // Make sure we're not already stopped before we start speaking
    if (SpeechManager.isActive(bubbleId)) {
      // Start speaking
      window.speechSynthesis.speak(utterance);
    }
  };

  // New handler for Generate Image Prompt
  const handleGenerateImagePrompt = (text) => {
    setSelectedPromptText(text);
    setImagePromptOpen(true);
    closeTooltip();
  };

  // Close image prompt generator dialog
  const handleCloseImagePrompt = () => {
    setImagePromptOpen(false);
  };

  //if bot response has error
  // eslint-disable-next-line react/prop-types
  const isErrorMessage =
    message?.toLowerCase().includes("something went wrong") ||
    message?.toLowerCase().includes("bad request") ||
    message?.toLowerCase().includes("error") ||
    message?.toLowerCase().includes("proper");
  // Helper function to determine message styling
  const getMessageStyle = () => {
    if (isSystemNotification) {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: "0.7rem",
        color: theme.palette.text.secondary,
      };
    }
    return {
      fontSize: ".9rem",
      fontFamily: "'Poppins', sans-serif",
      color: warningMessage ? "red" : theme.palette.text.primary,
    };
  };

  // Helper function to get container styles
  const getContainerStyles = () => {
    // Determine flex direction based on message type and user
    let flexDirection;
    if (isSystemNotification) {
      flexDirection = "column";
    } else if (isUser) {
      flexDirection = "row-reverse";
    } else {
      flexDirection = "row";
    }

    return {
      display: "flex",
      flexDirection: flexDirection,
      alignItems: isSystemNotification ? "center" : "flex-start",
      justifyContent: isSystemNotification ? "center" : "flex-start",
      mb: 2,
      sx: isSystemNotification ? { width: "105%" } : {}
    };
  };

  // Helper function to get message box styles
  const getMessageBoxStyles = () => {
    const baseStyles = {
      p: 1,
      border: getMessageBorder(),
      color: theme.palette.text.primary,
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: getBackgroundColor(),
      width: getMessageWidth(),
      ref: messageRef,
      onMouseUp: handleTextSelection,
      position: "relative"
    };

    if (isSystemNotification) {
      baseStyles.sx = {
        position: "relative",
        left: "-40%",
        transform: "translateX(50%)",
        maxWidth: "70%",
        margin: "0 auto",
        textAlign: "center"
      };
    }

    return baseStyles;
  };

  const getBackgroundColor = () => {
    if (isSystemNotification) return theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
    if (isUser) return "none";
    return theme.palette.mode === "dark" ? theme.palette.background.paper : "#F4FAFF";
  };

  const getMessageBorder = () => {
    if (isUser && !isSystemNotification) return `2px solid ${theme.palette.divider}`;
    if (!isUser && !isSystemNotification) return `2px solid ${theme.palette.chatBorder}`;
    if (isSystemNotification) return `1px dashed ${theme.palette.systemNotificationBorder}`;
    return "none";
  };

  const getMessageWidth = () => {
    if (isSystemNotification) return "100%";
    if (isUser) return "auto";
    return "105%";
  };



  // Component for file datasource section
  const FileDatasourceSection = () => {
    if (selectDatasourse !== "fileDatasource") return null;

    return (
      <Box>
        <Box display="flex" alignItems="center">
          <Typography
            style={{
              fontSize: ".9rem",
              fontFamily: "'Poppins', sans-serif",
              color: theme.palette.text.primary,
              fontWeight: "bold",
              display: toolResult?.length > 0 ? "block" : "none"
            }}
          >
            <br />Sources:
          </Typography>
          {hasAdditionalData && (
            <Tooltip title="View additional insights">
              <IconButton size="small" onClick={handleInfoClick} sx={{ ml: 1, p: 0 }}>
                <InfoIcon fontSize="small" sx={{ color: "#6366F1" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {toolResult?.length > 0 && toolResult?.map((item, index) => (
          <Typography
            key={index}
            style={{
              fontSize: ".9rem",
              fontFamily: "'Poppins', sans-serif",
              color: theme.palette.text.primary
            }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  // Component for additional responses popover
  const AdditionalResponsesPopover = () => (
    <Box>
      <Popover
        id={infoId}
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2,
            width: "80vw",
            maxWidth: "800px",
            maxHeight: "500px",
            overflow: "auto",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Additional Insights</Typography>
        <PopoverTabs />
        <PopoverContent />
        <PopoverCloseButton />
      </Popover>
    </Box>
  );

  // Component for popover tabs
  const PopoverTabs = () => (
    <Tabs
      value={activeTab}
      onChange={handleTabChange}
      sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
    >
      {additionalAgentResponses?.documents && <Tab label="Document Analysis" />}
      {additionalAgentResponses?.tastewise && <Tab label="Market Insights" />}
    </Tabs>
  );

  // Component for popover content
  const PopoverContent = () => (
    <>
      {activeTab === 0 && additionalAgentResponses?.documents && (
        <Typography
          component="div"
          sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", lineHeight: 1.6 }}
        >
          {additionalAgentResponses.documents}
        </Typography>
      )}
      {activeTab === 1 && additionalAgentResponses?.tastewise && (
        <Typography
          component="div"
          sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", lineHeight: 1.6 }}
        >
          {additionalAgentResponses.tastewise}
        </Typography>
      )}
    </>
  );

  // Component for popover close button
  const PopoverCloseButton = () => (
    <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
      <Button
        onClick={handleInfoClose}
        variant="contained"
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          background: "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)",
          "&:hover": { background: "linear-gradient(90deg, #5253d4 0%, #7949e0 100%)" }
        }}
      >
        Close
      </Button>
    </Box>
  );

  // Component for response time display
  const ResponseTimeDisplay = ({ isUser }) => {
    if (responseTime <= 0) return null;
    return (
      // <Tooltip title={`Response time: ${responseTime} seconds`}>
        <Typography
          variant="textSizeLightText"
          component="div"
          sx={{
            opacity: "50%",
            textAlign: "right",
            display: isUser ? "none" : "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            marginTop: "10px",
            fontSize: ".7rem"
          }}
        >
          <TimerOutlinedIcon sx={{ fontSize: "0.85rem" }} />
          {responseTime} sec
        </Typography>
      // </Tooltip>
    );
  };

  // Helper function to check if action buttons should be shown
  const shouldShowActionButtons = () => {
    return !isSystemNotification && !isUser && !isLoading && !isErrorMessage && !isFirstMessage;
  };

  // Component for action buttons section
  const ActionButtons = () => {
    if (!shouldShowActionButtons()) return null;

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ paddingTop: "10px" }}>
        {ReportSection()}
        {ControlButtons()}
        {CopyNotification()}
        {CitationDialog()}
      </Box>
    );
  };

  // Component for report section
  const ReportSection = () => {
    // console.log("Selected Data Source:", selectDatasourse);
    // const showReport = selectDatasourse.includes("tastewise") ||
    //   selectDatasourse.includes("mintel") ||
    //   selectDatasourse.includes("circana") ||
    //   selectDatasourse.includes("spins") ||
    //   selectDatasourse.includes("SPINS") ||
    //   selectDatasourse.includes("Yogi") ||
    //   selectDatasourse.includes("SproutSocial");

    // if (!showReport) return null;

    return (
      <Box>
        <Box
          display="flex"
          alignItems="center"
          style={{
            cursor: "pointer",
            fontFamily: "'Poppins', sans-serif",
            color: "#667085",
            fontSize: "14px",
            visibility: toolResults.length > 0 ? "visible" : "hidden"
          }}
          onClick={handleReportDialogOpen}
        >
          <img
            src={report_nexus}
            alt="Report Icon"
            style={{ marginRight: "8px", width: "20px", height: "20px" }}
          />
          <span>Detailed View</span>
        </Box>
        <ReportDialog
          open={reportDialogOpen}
          handleClose={handleReportDialogClose}
          toolResults={toolResults}
          additional_agent_responses={additional_agent_responses}
          reformatted_question={reformatted_question}
        />
      </Box>
    );
  };

  // Component for control buttons
  const ControlButtons = () => (
    <Box sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 2 }}>
      {FeedbackButtons()}
      {ReadAloudButton()}
      {CopyButton()}
      {ImagePromptButton()}
    </Box>
  );

  const FeedbackButtons = () => (
    <>
      <Tooltip title="Helpful response">
        <span>
          <IconButton
            onClick={handleThumbsUpClick}
            size="medium"
            disabled={isSubmittingFeedback || !questionId || !threadId}
            sx={{ color: feedbackType === "up" ? "#2e7d32" : "gray", padding: 0 }}
          >
            {feedbackType === "up" ? <ThumbUpAltIcon fontSize="small" /> : <ThumbUpAltOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Needs improvement">
        <span>
          <IconButton
            onClick={handleThumbsDownOpen}
            size="medium"
            disabled={isSubmittingFeedback || !questionId || !threadId}
            sx={{ color: feedbackType === "down" ? "#c62828" : "gray", padding: 0 }}
          >
            {feedbackType === "down" ? <ThumbDownAltIcon fontSize="small" /> : <ThumbDownAltOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={feedbackDialogOpen}
        onClose={handleFeedbackDialogClose}
        maxWidth="sm"
        fullWidth
        keepMounted
      >
        <DialogTitle>Tell us what went wrong</DialogTitle>
        <DialogContent
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
            <FormLabel component="legend">Feedback reason</FormLabel>
            <RadioGroup
              value={selectedFeedbackReason}
              onChange={(event) => setSelectedFeedbackReason(event.target.value)}
            >
              {NEGATIVE_FEEDBACK_OPTIONS.map((option) => (
                <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
              ))}
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            label={selectedFeedbackReason === "Other" ? "Please provide details" : "Additional comments (optional)"}
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
          />
          {feedbackError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {feedbackError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFeedbackDialog} disabled={isSubmittingFeedback}>Cancel</Button>
          <Button onClick={handleThumbsDownSubmit} variant="contained" disabled={isSubmittingFeedback}>
            {isSubmittingFeedback ? "Saving..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Component for read aloud button
  const ReadAloudButton = () => (
    <Tooltip
      title={
        isReading
          ? "Stop Reading"
          : otherBubbleReading
            ? "Click to switch to this summary"
            : "Read Aloud"
      }
    >
      <IconButton
        onClick={handleReadAloud}
        size="medium"
        sx={{
          color: isReading
            ? "rgb(25, 118, 210)"
            : otherBubbleReading
              ? "rgba(0, 0, 0, 0.6)"
              : "gray",
          padding: 0
        }}
      >
        {isReading ? <VolumeOffIcon fontSize="medium" /> : <VolumeUpIcon fontSize="medium" />}
      </IconButton>
    </Tooltip>
  );

  // Component for copy button
  const CopyButton = () => (
    <Tooltip title="Copy to clipboard">
      <IconButton
        onClick={handleCopy}
        size="medium"
        sx={{ color: "gray", padding: 0, cursor: "pointer" }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Component for image prompt button
  const ImagePromptButton = () => {
    if (display_image_prompt !== "TRUE") return null;
    return (
      <Tooltip title="Generate image from this response">
        <IconButton
          onClick={() => handleGenerateImagePrompt("")}
          size="medium"
          sx={{ color: "gray", padding: 0, cursor: "pointer" }}
        >
          <AutoAwesomeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  // Component for copy notification
  const CopyNotification = () => (
    <Snackbar
      open={copySuccess || Boolean(feedbackError && !feedbackDialogOpen)}
      autoHideDuration={3000}
      onClose={() => {
        handleCloseCopyNotification();
        setFeedbackError("");
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        zIndex: 2,
        top: "77% !important",
        left: "43% !important",
        bottom: "0% !important",
        "& .MuiSnackbarContent-root": {
          boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.2)"
        }
      }}
    >
      <Alert
        onClose={() => {
          handleCloseCopyNotification();
          setFeedbackError("");
        }}
        severity={feedbackError && !feedbackDialogOpen ? "error" : "success"}
        sx={{
          width: "100%",
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500
        }}
      >
        {feedbackError && !feedbackDialogOpen ? feedbackError : "Content copied to clipboard!"}
      </Alert>
    </Snackbar>
  );

  // Component for citation dialog
  const CitationDialog = () => (
    <Dialog
      open={citationopen}
      onClose={handleCitationClose}
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" }
      }}
      PaperProps={{
        style: { boxShadow: "none", width: "80%", maxWidth: "none" }
      }}
    >
      <CitationDialogContent />
    </Dialog>
  );

  // Component for citation dialog content
  const CitationDialogContent = () => (
    <>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span style={{ fontWeight: "bold" }}>Citation Data Check</span>
          <IconButton
            onClick={handleCitationClose}
            size="small"
            sx={{ p: 0.5, ml: 1 }}
            aria-label="close"
          >
            <img src={closeicon} style={{ width: "20px" }} alt="" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <CitationContent />
        <DownloadButton />
      </DialogContent>
    </>
  );

  // Component for citation content
  const CitationContent = () => {
    if (tables.paragraph) {
      return (
        <Typography
          style={{
            fontFamily: "'Poppins', sans-serif",
            padding: "10px"
          }}
        >
          {tables.paragraph}
        </Typography>
      );
    }

    return Object.keys(tables).map((tableName, index) => {
      const data = tables[tableName];
      if (!data || data.length === 0 || (Array.isArray(data) && data.flat().length === 0)) {
        return null;
      }

      return (
        <div key={index}>
          <h4 dangerouslySetInnerHTML={{ __html: tableName }} />
          <TableContainer
            component={Paper}
            style={{ maxHeight: "350px", overflow: "auto" }}
          >
            <DataTable data={data} />
          </TableContainer>
        </div>
      );
    });
  };

  // Component for download button
  const DownloadButton = () => {
    if (tables.paragraph) return null;
    return (
      <Box display="flex" justifyContent="flex-end" sx={{ marginTop: "10px" }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleDownloadAll}
          style={{ fontFamily: "'Poppins', sans-serif" }}
          sx={{
            borderRadius: "25px",
            height: "52px",
            width: "20%",
            background: "linear-gradient(90deg, #E13C25 100%, #FF825A 100%)",
            boxShadow: "0px 21px 27px -10px rgba(96, 60, 255, 0.48)",
            textTransform: "none",
            "&:active": { background: "linear-gradient(90deg, #E13C25 100%, #FF825A 100%)" },
            "&:hover": { background: "linear-gradient(90deg, #E13C25 100%, #FF825A 100%)" }
          }}
        >
          Download CSV
        </Button>
      </Box>
    );
  };

  // eslint-disable-next-line react/prop-types  
  const warningMessage =
    message?.toLowerCase().includes("bad request") ||
    message?.toLowerCase().includes("error");

  return (
    <Box {...getContainerStyles()}>
      {!isSystemNotification && (
        <Avatar src={isUser ? useravatar : aiavatar} />
      )}
      <Box
        ml={!isSystemNotification ? (isUser ? 0 : 2) : 0}
        mr={!isSystemNotification ? (isUser ? 2 : 7) : 0}
        sx={
          isSystemNotification
            ? { width: "70%", display: "flex", justifyContent: "center" }
            : {}
        }
      >
        <Box {...getMessageBoxStyles()}>
          <MessageContent
            isUser={isUser}
            isLoading={isLoading}
            index={index}
            getMessageStyle={getMessageStyle}
            message={message}
            tooltipOpen={tooltipOpen}
            display_image_prompt={display_image_prompt}
            tooltipPosition={tooltipPosition}
            selectedText={selectedText}
            closeTooltip={closeTooltip}
            handleGenerateImagePrompt={handleGenerateImagePrompt}
            imagePromptOpen={imagePromptOpen}
            handleCloseImagePrompt={handleCloseImagePrompt}
            selectedPromptText={selectedPromptText}
            FileDatasourceSection={FileDatasourceSection}
            AdditionalResponsesPopover={AdditionalResponsesPopover}
            ResponseTimeDisplay={ResponseTimeDisplay}
            onMessageLinkClick={handleMessageLinkClick}
            visualization_payload={visualization_payload}
          />
        </Box>
        {ActionButtons()}
      </Box>
    </Box>
  );
};

ChatBubble.propTypes = {
  message: PropTypes.string,
  additionalAgentResponses: PropTypes.array,
  responseTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  citmessage: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isUser: PropTypes.bool,
  isSystemNotification: PropTypes.bool,
  isLoading: PropTypes.bool,
  handleCitationClickOpen: PropTypes.func,
  citationopen: PropTypes.bool,
  handleCitationClose: PropTypes.func,
  citationclickvisible: PropTypes.bool,
  tables: PropTypes.object,
  handleDownloadAll: PropTypes.func,
  isFirstMessage: PropTypes.bool,
  toolResult: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.any),
    PropTypes.object
  ]),
  selectDatasourse: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  additional_agent_responses: PropTypes.object,
  reformatted_question: PropTypes.string,
  index: PropTypes.number,
  onRegenerateClick: PropTypes.func,
  isLastBotMessage: PropTypes.bool,
  onAbbreviationCandidateClick: PropTypes.func,
  questionId: PropTypes.string,
  threadId: PropTypes.string,
  feedbackType: PropTypes.string,
  feedbackReason: PropTypes.string,
  feedbackComment: PropTypes.string,
  onSubmitFeedback: PropTypes.func,
  visualization_payload: PropTypes.object,
};



export default ChatBubble;
